import { Navigate, Route, Routes } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import { RequireSceneForgeAccess } from './components/RequireSceneForgeAccess';
import { Spinner } from './components/Spinner';
import ExportToast from './components/ExportToast';
import { TopFade } from './components/TopFade';

// SceneForge route map (V5 — SCENEFORGE_V5_PLATFORM.md §8.2).
//
// Auth / public routes (NO RequireSceneForgeAccess wrapper):
//   /login           — magic-link + OTP entry
//   /auth/callback   — Supabase PKCE callback
//   /accept-invite   — Path B (net-new partner) acceptance flow
//   /not-provisioned — hard-wall page for signed-in-but-not-provisioned users
//
// Guarded routes (wrapped in RequireSceneForgeAccess):
//   /dashboard                          — productions list (V5)
//   /jobs/new                           — production create form
//   /jobs/:productionId                 — production detail (clips + extractions)
//   /jobs/:productionId/source-clips/new — 3-step source clip wizard
//   /jobs/:productionId/clean-plates/new — Clean Plate wizard (standalone
//                                          Seedance vehicle-removal with
//                                          optional Gemini-merged special
//                                          request — migration 170)
//   /garage                             — team garage (car references)
//   /account                            — current user + active team summary
//   /team-select                        — multi-membership team picker (allowNoTeam)
//   /admin                              — admin-only team management (adminOnly)
//
// Legacy V4 routes still mounted for back-compat — clicking an extraction
// from JobDetail's Section 2 hands off to /exports/:id which the existing
// page renders unchanged.
//   /exports/new                        — manual extraction upload form
//   /exports/:id                        — per-extraction detail with Realtime
//
// Everything else → 404.

const Login = lazy(() => import('./pages/Login'));
const AuthCallback = lazy(() => import('./pages/AuthCallback'));
const AcceptInvite = lazy(() => import('./pages/AcceptInvite'));
const NotProvisioned = lazy(() => import('./pages/NotProvisioned'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const NewExport = lazy(() => import('./pages/NewExport'));
const ExportDetail = lazy(() => import('./pages/ExportDetail'));
const NotFound = lazy(() => import('./pages/NotFound'));
// V5 pages
const TeamSelect = lazy(() => import('./pages/TeamSelect'));
const Account = lazy(() => import('./pages/Account'));
const Admin = lazy(() => import('./pages/Admin'));
const NewJob = lazy(() => import('./pages/NewJob'));
const JobDetail = lazy(() => import('./pages/JobDetail'));
const NewSourceClip = lazy(() => import('./pages/NewSourceClip'));
const NewCleanPlate = lazy(() => import('./pages/NewCleanPlate'));
const Garage = lazy(() => import('./pages/Garage'));
const Locations = lazy(() => import('./pages/Locations'));

function PageLoader() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-zinc-950">
      <Spinner size={20} />
    </div>
  );
}

export default function App() {
  return (
    <>
      {/* Whisper-quiet light-gray gradient stretching ~200px down from
          the very top of the viewport behind the TopBar. Replaces the
          earlier WebGL aurora (felt too consumer-app for SceneForge's
          pro grayscale aesthetic). Pure CSS, pointer-events:none,
          click-through. */}
      <TopFade />
      <Suspense fallback={<PageLoader />}>
        <Routes>
        {/* Public / auth */}
        <Route path="/login" element={<Login />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/accept-invite" element={<AcceptInvite />} />
        <Route path="/not-provisioned" element={<NotProvisioned />} />

        {/* Root redirect */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />

        {/* V5 guarded routes */}
        <Route
          path="/dashboard"
          element={
            <RequireSceneForgeAccess>
              {(user) => <Dashboard user={user} />}
            </RequireSceneForgeAccess>
          }
        />
        <Route
          path="/team-select"
          element={
            <RequireSceneForgeAccess allowNoTeam>
              {(user) => <TeamSelect user={user} />}
            </RequireSceneForgeAccess>
          }
        />
        <Route
          path="/account"
          element={
            <RequireSceneForgeAccess>
              {(user) => <Account user={user} />}
            </RequireSceneForgeAccess>
          }
        />
        <Route
          path="/admin"
          element={
            <RequireSceneForgeAccess adminOnly allowNoTeam>
              {(user) => <Admin user={user} />}
            </RequireSceneForgeAccess>
          }
        />
        <Route
          path="/jobs/new"
          element={
            <RequireSceneForgeAccess>
              {(user) => <NewJob user={user} />}
            </RequireSceneForgeAccess>
          }
        />
        <Route
          path="/jobs/:productionId"
          element={
            <RequireSceneForgeAccess>
              {(user) => <JobDetail user={user} />}
            </RequireSceneForgeAccess>
          }
        />
        <Route
          path="/jobs/:productionId/source-clips/new"
          element={
            <RequireSceneForgeAccess>
              {(user) => <NewSourceClip user={user} />}
            </RequireSceneForgeAccess>
          }
        />
        <Route
          path="/jobs/:productionId/clean-plates/new"
          element={
            <RequireSceneForgeAccess>
              {(user) => <NewCleanPlate user={user} />}
            </RequireSceneForgeAccess>
          }
        />
        <Route
          path="/garage"
          element={
            <RequireSceneForgeAccess>
              {(user) => <Garage user={user} />}
            </RequireSceneForgeAccess>
          }
        />
        <Route
          path="/locations"
          element={
            <RequireSceneForgeAccess>
              {(user) => <Locations user={user} />}
            </RequireSceneForgeAccess>
          }
        />

        {/* Legacy V4 extraction routes — JobDetail's Section 2 links here */}
        <Route
          path="/exports/new"
          element={
            <RequireSceneForgeAccess>
              {(user) => <NewExport user={user} />}
            </RequireSceneForgeAccess>
          }
        />
        <Route
          path="/exports/:id"
          element={
            <RequireSceneForgeAccess>
              {(user) => <ExportDetail user={user} />}
            </RequireSceneForgeAccess>
          }
        />

        {/* 404 */}
        <Route path="*" element={<NotFound />} />
        </Routes>

        {/* Global toast surface — consumes events from the `toast` bus exposed by
            components/ExportToast. Mounted once at app root so it survives route
            transitions and is always live. See SCENEFORGE_OPTIMIZE.md §3d. */}
        <ExportToast />
      </Suspense>
    </>
  );
}
